<script setup lang="ts">
import { ref, watch } from 'vue';
import { useEditGroup } from '@ilteducation/customer-mgmt';
import PermissionLock from '../PermissionLock.vue';
import type { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { checkGroupUpdatePermission } from '@ilteducation/customer-mgmt-permissions';

import useToast from '../../composables/useToast';
import { useLocale } from '../../composables/useLocale';
import useModal, { modals } from '../../composables/useModal';
import { usePermissions } from '../../composables/usePermissions';
import { getGroupCodeAndPasswordHelpURL } from '../../helpers/urls';

const { t } = useLocale();
const { toast } = useToast();
const { updateGroup } = useEditGroup();
const { permissionContext, reporterWithReason, getCallerAssociation, getGroupMemberships } = usePermissions();

const isUpdating = ref(false);

const props = defineProps<{ group: CustomerMgmtTypes.GetGroupResponse }>();
const emits = defineEmits<{ (event: 'groupUpdated', value: string): void }>();

const isGroupCodeEditableCheck = ref<ReturnType<typeof reporterWithReason>>({ allowed: true, reason: undefined });
watch(
  () => props.group,
  () => {
    checkGroupUpdatePermission(
      permissionContext.value,
      {
        ...props.group,
        organization: props.group.schoolUnit,
      },
      { generateCode: false },
      getCallerAssociation,
      getGroupMemberships,
      reporterWithReason,
    ).then((res) => {
      isGroupCodeEditableCheck.value = res;
    });
  },
  { immediate: true },
);

const { close: closeEditGroupLoginModal } = useModal(modals.EDIT_GROUP_LOGIN);
const { open: openConfirmDeleteModal } = useModal(modals.CONFIRM_DELETE_GROUP_CODE);

const createGroupcode = async () => {
  isUpdating.value = true;
  await updateGroup(props.group.uid, { generateCode: true });
  isUpdating.value = false;
  emits('groupUpdated', props.group.uid);
  toast(t('lcp.group_code_generated_successfully'), 'success');
};

const confirmDelete = () => {
  closeEditGroupLoginModal();
  openConfirmDeleteModal();
};

const removeGroupcode = async () => {
  isUpdating.value = true;
  await updateGroup(props.group.uid, { generateCode: false });
  isUpdating.value = false;
  emits('groupUpdated', props.group.uid);
  toast(t('lcp.group_code_removed_successfully'), 'success');
};

defineExpose({ isUpdating, removeGroupcode });
</script>

<template>
  <v-row no-gutters>
    <v-col cols="12" class="d-flex ga-2 align-center pb-4">
      <v-fab-transition>
        <v-icon v-show="group?.code" :key="group?.code || group?.uid" color="success" size="x-small">
          mdi-check-circle
        </v-icon>
      </v-fab-transition>
      <span class="font-weight-bold">{{ t('lcp.group_code') }}</span>
      <v-scroll-y-transition :duration="1000" leave-absolute hide-on-leave>
        <span v-if="group?.code" :key="group?.code">{{ group?.code }}</span>
      </v-scroll-y-transition>
    </v-col>
    <v-col cols="12" class="pb-4">
      <p>{{ t('lcp.group_code_info') }}</p>
      <v-btn
        variant="text"
        height="1em"
        class="px-0 text-decoration-underline"
        :href="getGroupCodeAndPasswordHelpURL()"
        target="_blank"
      >
        {{ t('lcp.learn_more_about_group_logins') }}
      </v-btn>
    </v-col>
    <v-scroll-y-transition leave-absolute>
      <v-col v-if="group?.code" cols="12" class="d-flex ga-4">
        <v-row>
          <v-col cols="auto" class="d-flex ga-2">
            <v-chip
              :disabled="isUpdating || !isGroupCodeEditableCheck.allowed"
              prepend-icon="mdi-close"
              class="mx-0"
              @click="confirmDelete"
            >
              {{ t('lcp.remove') }}
            </v-chip>
            <v-chip
              :disabled="isUpdating || !isGroupCodeEditableCheck.allowed"
              prepend-icon="mdi-reload"
              class="mx-0"
              @click="createGroupcode"
            >
              {{ t('lcp.generate_new') }}
            </v-chip>
          </v-col>
          <v-col v-if="!isGroupCodeEditableCheck.allowed" cols="auto" class="d-flex align-center justify-end pl-0">
            <PermissionLock :reason="isGroupCodeEditableCheck.reason" />
          </v-col>
        </v-row>
      </v-col>
      <v-col v-else cols="12">
        <v-row>
          <v-col cols="auto">
            <v-chip
              :disabled="isUpdating || !isGroupCodeEditableCheck.allowed"
              prepend-icon="mdi-plus"
              class="mx-0"
              @click="createGroupcode"
            >
              {{ t('lcp.add_group_code') }}
            </v-chip>
          </v-col>
          <v-col v-if="!isGroupCodeEditableCheck.allowed" cols="auto" class="d-flex align-center justify-end pl-0">
            <PermissionLock :reason="isGroupCodeEditableCheck.reason" />
          </v-col>
        </v-row>
      </v-col>
    </v-scroll-y-transition>
  </v-row>
</template>
